import { useQuery } from '@probatix/common/utils/url'

import { useDispatch } from 'react-redux'
import { matchRoutes, useNavigate } from 'react-router-dom'

import { AuthenticatedUser } from '@probatix/common/services/toolkit/auth/auth.model'
import useLocaleAwareRoute from '@probatix/common/hooks/useLocaleAwareRoute'

import useBoolean from '@probatix/common/hooks/useBoolean'

import { authServiceActions } from '@probatix/common/services/toolkit/auth'
import { getToken, removeToken } from '@probatix/common/services/services.helpers'

import { ENTRYPOINT } from '@probatix/myprobatix/config/app'
import Routing, { Route } from '@probatix/myprobatix/core/routings'
import { ActionParams } from '@probatix/common/utils/service'
import { privateLandingPage, specialPublicLandingPage } from '@probatix/myprobatix/constants/page'

const pathsToPublicCheckout = [
  '/booking',
  '/checkout', '/check-result',
  '/manage-booking']

const useAuth = (params?: ActionParams<any, { criteria }>) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { generatePathWithLocale } = useLocaleAwareRoute()
  const { queryParams } = useQuery<{ returnUrl?: string }>()

  const [authenticated, setAuthenticatedTrue, setAuthenticatedFalse] = useBoolean(false)
  const [authInitialized, setAuthInitializedTrue, setAuthInitializedFalse] = useBoolean(false)

  const logout = () => {
    setAuthInitializedFalse()
    dispatch(authServiceActions.logout())

    const isPathIncluded = pathsToPublicCheckout.some((path) => window.location.pathname.includes(path))
    if (!isPathIncluded) {
      removeToken()
    }

    setAuthenticatedFalse()
    const matchedRoutes = matchRoutes(Routing, window.location.pathname) || []
    matchedRoutes.forEach((m: Route) => {
      const { route } = m

      if ((!route.path
        || !window.location.pathname.includes(route.path as string))
        && (!route.path!.includes('/:testUuid') && !route.path!.includes('/:loincNum')
        )
      ) {
        return
      }

      // NOTE: if page is not public than redirect to login, for example if user is on the /signup page
      // than there is no need to redirect
      if (!route?.public) {
        navigate(generatePathWithLocale(specialPublicLandingPage))
      }
    })
  }

  const handleAuthInitialized = () => {
    setAuthInitializedTrue()
  }

  const handleSubmitSuccess = (response: AuthenticatedUser) => {
    setAuthenticatedTrue()
    setTimeout(() => {
      dispatch(authServiceActions.setAuthData(response))
      if (queryParams.returnUrl) {
        navigate(generatePathWithLocale(queryParams.returnUrl))
      }

      // NOTE: if user is signed in redirect to the dashboard homepage
      if (0 <= [
        generatePathWithLocale(''),
        generatePathWithLocale('login'),
        generatePathWithLocale('signup'),
        generatePathWithLocale('registration/confirm'),
        generatePathWithLocale('password-reset'),
      ].indexOf(
        window.location.pathname)
      ) {
        navigate(generatePathWithLocale(privateLandingPage))
      }
    }, 100)
  }

  const authenticateUser = () => {
    dispatch(authServiceActions.me({
      ...(params?.customApiPrefix && {
        customApiPrefix: params.customApiPrefix,
      }),
      customEntrypoint: ENTRYPOINT,
      onErrorCallback: () => logout(),
      onFinallyCallback: handleAuthInitialized,
      onSuccessCallback: (response: AuthenticatedUser) => handleSubmitSuccess(response),
    }))
  }

  const isAuthenticated = () => !!getToken()

  const checkAuth = () => {
    if (null !== getToken()) {
      authenticateUser()

      return
    }
    logout()
  }

  return {
    authInitialized,
    authenticateUser,
    authenticated,
    checkAuth,
    isAuthenticated,
    logout,
  }
}

export default useAuth
